import type { ShiftPeriod } from './types';
import { isShiftCell, roleColumnDataIndex } from './lib/util';
import { useEffect, useRef, useState } from 'react';
import { useSize } from 'ahooks';
import type { ShiftViolationTableRecord } from './ViolationTable';
import { shiftDateDataIndexFormat } from '../../lib/utils/date-time';
import { parse } from 'date-fns';
import { shiftBackgroundColours } from '../../theme';
import ViolationDiv from './ViolationDiv';
import { userFullName } from '../../lib/utils';

type ViolationCellProps = {
  readonly rosterTableRowRecord: ShiftViolationTableRecord;
  readonly dataIndex: string;
  readonly dayShift: ShiftPeriod;
  // To be passed to the cell width effect as a dependency
  readonly refresh: readonly unknown[];
};

/**
 * Renders a table cell that displays a piece of shift information depending on the supplied data index and current row record.
 * The cell value is extracted from the row record with data index.
 * E.g. record[roleColumnDataIndex] renders role name or user name. record['20220725'] renders the Shift data with custom rendering logic.
 *
 * This cell is to be used in the violations table to show shifts, with and without
 * fatigue violations
 *
 */
const ViolationCell = ({
  rosterTableRowRecord,
  dataIndex,
  dayShift,
  refresh,
}: ViolationCellProps) => {
  const cellDivRef = useRef<HTMLDivElement>(null);

  const [cellDivWidthInPixel, setCellDivWidth] = useState<number>(0);

  const windowSize = useSize(document.querySelector('body'));

  // Calculates the current cell's <div> DOM width, used to determine the Shift button width based on length of the Shift.
  useEffect(() => {
    const currentCellDivWidth = cellDivRef.current?.clientWidth;

    if (
      currentCellDivWidth !== undefined &&
      (windowSize?.width ?? 0) > 0 &&
      cellDivWidthInPixel !== currentCellDivWidth
    ) {
      setCellDivWidth(currentCellDivWidth);
    }
  }, [cellDivWidthInPixel, windowSize, refresh]);

  if (
    dataIndex === roleColumnDataIndex &&
    rosterTableRowRecord.userRecord?.firstName !== undefined
  ) {
    return (
      <div style={{ paddingLeft: '1rem' }}>
        {userFullName({
          first_name: rosterTableRowRecord.userRecord.firstName,
          last_name: rosterTableRowRecord.userRecord.lastName,
        })}
      </div>
    );
  }

  const selectedDate = parse(dataIndex, shiftDateDataIndexFormat, new Date());

  const maybeShifts = rosterTableRowRecord.shifts[dataIndex];

  if (isShiftCell(maybeShifts)) {
    const userCellRecord = {
      selectedDate,
      userId: rosterTableRowRecord.userId,
      firstName: rosterTableRowRecord.userRecord?.firstName,
      lastName: rosterTableRowRecord.userRecord?.lastName,
    };

    return (
      <div
        ref={cellDivRef}
        style={{
          width: '100%',
          display: 'inline-block',
          position: 'relative',
        }}
        key={`${dataIndex}-${
          rosterTableRowRecord.userRecord?.firstName ?? ''
        }-shift`}
      >
        {maybeShifts?.map((shift, index) => (
          <ViolationDiv
            index={index}
            violations={{
              consecutiveShiftViolation: shift.consecutiveShiftViolation,
              maxShiftHoursViolation: shift.maxShiftHoursViolation,
              shiftRestGapViolation: shift.shiftRestGapViolation,
            }}
            key={`${shift.id}-filled`}
            cellWidth={cellDivWidthInPixel}
            cellRecord={{
              ...userCellRecord,
              teamRoleAssignmentId: shift.team?.id ?? 'not found',
              roleName: shift.teamRoleName ?? 'not found',
            }}
            team={shift.team}
            shift={shift}
            dayShift={dayShift}
            shiftBackgroundColours={shiftBackgroundColours}
          />
        ))}
      </div>
    );
  }
  return <></>;
};

export default ViolationCell;
